import React, { useMemo, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';

const EDGE_FN_NAME = 'create-user'; // change if your function has a different name

const redact = (t) => (t ? `${t.slice(0, 10)}…${t.slice(-8)}` : '—');

// very light JWT decode (payload only; safe for diagnostics)
const decodeJwt = (token) => {
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
};

const PlatformAdminTester = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Probe payload controls
  const [probeEmail, setProbeEmail] = useState('probe.user@acme.com');
  const [probeName, setProbeName] = useState('Probe User');
  const [probePassword, setProbePassword] = useState('Temp1234!');
  const [probeRole, setProbeRole] = useState('company_user');
  const [probeCompanyId, setProbeCompanyId] = useState('');

  const viteUrl = import.meta.env?.VITE_SUPABASE_URL;
  const viteAnon = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  // Correctly derive the functions domain from https://<ref>.supabase.co
  const edgeUrl = useMemo(() => {
    if (!viteUrl) return '';
    // Convert https://<ref>.supabase.co → https://<ref>.functions.supabase.co
    const base = viteUrl.replace(
      /^https:\/\/([^.]+)\.supabase\.co$/,
      'https://$1.functions.supabase.co'
    );
    return `${base}/${EDGE_FN_NAME}`;
  }, [viteUrl]);

  const updateResult = (patch) =>
    setResult((prev) => ({ ...(prev || {}), ...patch, timestamp: new Date().toISOString() }));

  // Shared debug info (current session/user/profile)
  const baseDebug = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token || null;
    const jwt = token ? decodeJwt(token) : null;

    const { data: me, error: meErr } = await supabase.auth.getUser();
    const meId = me?.user?.id || null;

    const { data: profile, error: profileErr } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', meId || '')
      .single();

    return { token, jwt, me, meErr, profile, profileErr };
  };

  const testRpcDefaultClient = async () => {
    setLoading(true);
    setResult(null);
    try {
      const dbg = await baseDebug();

      if (dbg.meErr || !dbg.me?.user) {
        updateResult({
          success: false,
          phase: 'rpc-default',
          error: dbg.meErr?.message || 'No authenticated user. Please sign in.',
          debug: { token: redact(dbg.token) },
        });
        return;
      }

      const { data, error } = await supabase.rpc('is_platform_admin_from_auth');

      updateResult({
        success: !error,
        phase: 'rpc-default',
        rpcResult: data ?? null,
        error: error ? `RPC Error: ${error.message} (${error.code || 'no-code'})` : null,
        debug: {
          token: redact(dbg.token),
          jwtClaims: dbg.jwt,
          userId: dbg.me.user.id,
          userEmail: dbg.profile?.email || null,
          userRole: dbg.profile?.role || null,
          profileError: dbg.profileErr?.message || null,
          client: 'default supabase client',
        },
      });
    } catch (err) {
      updateResult({
        success: false,
        phase: 'rpc-default',
        error: `Unexpected Error: ${err?.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const testRpcWithCustomHeaders = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || null;

      if (sessionErr || !token) {
        updateResult({
          success: false,
          phase: 'rpc-custom',
          error: sessionErr?.message || 'No active session found. Please sign in.',
        });
        return;
      }

      const client = createClient(viteUrl, viteAnon, {
        global: { headers: { Authorization: `Bearer ${token}` } },
      });

      const { data, error } = await client.rpc('is_platform_admin_from_auth');

      updateResult({
        success: !error,
        phase: 'rpc-custom',
        rpcResult: data ?? null,
        error: error
          ? `RPC Error (custom headers): ${error.message} (${error.code || 'no-code'})`
          : null,
        debug: {
          token: redact(token),
          client: 'new client with Authorization header',
        },
      });
    } catch (err) {
      updateResult({
        success: false,
        phase: 'rpc-custom',
        error: `Unexpected Error: ${err?.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Preferred probe: supabase.functions.invoke auto-uses correct domain and sends JWT
  const probeEdgeFunction = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || null;
      if (!token) {
        updateResult({
          success: false,
          phase: 'edge-probe',
          error: 'No active session. Please sign in.',
        });
        return;
      }

      const payload = {
        email: probeEmail,
        password: probePassword,
        full_name: probeName,
        role: probeRole,
        company_id: probeCompanyId || null,
      };

      const { data, error } = await supabase.functions.invoke(EDGE_FN_NAME, {
        body: payload,
      });

      const httpStatus = error?.context?.status ?? 200;

      updateResult({
        success: !error,
        phase: 'edge-probe',
        httpStatus,
        error: error ? (data?.error || error.message || `HTTP ${httpStatus}`) : null,
        debug: {
          url: edgeUrl, // just to display what we'd expect the URL to be
          payload: { ...payload, password: '********' },
          token: redact(token),
          response: data,
          note:
            '200/201 = created; 409 = email exists; 403 = not platform_admin; 401 = bad/missing JWT; 415 = wrong content-type',
        },
      });
    } catch (err) {
      updateResult({
        success: false,
        phase: 'edge-probe',
        error: `Unexpected Error: ${err?.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Alternate manual fetch probe (kept for completeness; not used by default)
  const probeEdgeFunctionViaFetch = async () => {
    setLoading(true);
    setResult(null);
    try {
      if (!edgeUrl) {
        updateResult({
          success: false,
          phase: 'edge-probe',
          error: 'Edge Function URL is empty. Check VITE_SUPABASE_URL.',
        });
        return;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || null;
      if (!token) {
        updateResult({
          success: false,
          phase: 'edge-probe',
          error: 'No active session. Please sign in.',
        });
        return;
      }

      const payload = {
        email: probeEmail,
        password: probePassword,
        full_name: probeName,
        role: probeRole,
        company_id: probeCompanyId || null,
      };

      const res = await fetch(edgeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));

      updateResult({
        success: res.ok,
        phase: 'edge-probe',
        httpStatus: res.status,
        error: res.ok ? null : body?.error || `HTTP ${res.status}`,
        debug: {
          url: edgeUrl,
          payload: { ...payload, password: '********' },
          token: redact(token),
          response: body,
          note:
            '200/201 = created; 409 = email exists; 403 = not platform_admin; 401 = bad/missing JWT; 415 = wrong content-type',
        },
      });
    } catch (err) {
      updateResult({
        success: false,
        phase: 'edge-probe',
        error: `Unexpected Error: ${err?.message}`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold mb-2 text-gray-900">
        Platform Admin – RPC & Edge Function Tester
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Verifies <code className="bg-gray-100 px-1 rounded">is_platform_admin_from_auth</code> (default & custom header clients)
        and probes the <code className="bg-gray-100 px-1 rounded">{EDGE_FN_NAME}</code> Edge Function with your current JWT.
      </p>

      {/* Edge Function payload controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-600 mb-1">Probe Email</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={probeEmail}
            onChange={(e) => setProbeEmail(e.target.value)}
            placeholder="new.user@acme.com"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Full Name</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={probeName}
            onChange={(e) => setProbeName(e.target.value)}
            placeholder="New User"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Password</label>
          <input
            className="w-full border rounded px-2 py-1"
            type="password"
            value={probePassword}
            onChange={(e) => setProbePassword(e.target.value)}
            placeholder="Temp1234!"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Role</label>
          <select
            className="w-full border rounded px-2 py-1"
            value={probeRole}
            onChange={(e) => setProbeRole(e.target.value)}
          >
            <option value="company_user">company_user</option>
            <option value="company_admin">company_admin</option>
            <option value="platform_admin">platform_admin</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-600 mb-1">Company ID (optional)</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={probeCompanyId}
            onChange={(e) => setProbeCompanyId(e.target.value)}
            placeholder="UUID or leave blank"
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Button onClick={testRpcDefaultClient} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
          {loading ? 'Testing…' : 'Test RPC (default client)'}
        </Button>

        <Button onClick={testRpcWithCustomHeaders} disabled={loading} variant="outline">
          {loading ? 'Testing…' : 'Test RPC (custom headers)'}
        </Button>

        <Button onClick={probeEdgeFunction} disabled={loading} className="bg-purple-600 hover:bg-purple-700">
          {loading ? 'Probing…' : 'Probe Edge Function'}
        </Button>

        {/* Optional: keep manual fetch path visible for comparison */}
        <Button onClick={probeEdgeFunctionViaFetch} disabled={loading} variant="ghost">
          {loading ? 'Probing…' : 'Probe via fetch (alt)'}
        </Button>
      </div>

      {/* Results */}
      {result && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium">Status:</span>
            <span
              className={`px-2 py-1 rounded text-sm ${
                result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {result.success ? 'SUCCESS' : 'ERROR'}
            </span>
            <span className="ml-2 text-xs text-gray-500">{result.phase || '—'}</span>
          </div>

          {'httpStatus' in result && (
            <div className="mb-2 text-sm">
              <span className="font-medium">HTTP Status:</span> {result.httpStatus ?? '—'}
            </div>
          )}

          <div className="mb-2 text-sm">
            <span className="font-medium">RPC Result:</span>{' '}
            <code className="bg-gray-100 px-2 py-1 rounded">
              {result?.rpcResult !== undefined ? String(result.rpcResult) : 'n/a'}
            </code>{' '}
            <span className="ml-1 text-xs text-gray-600">(true = platform admin)</span>
          </div>

          {!!result?.error && (
            <div className="mb-2 text-sm">
              <span className="font-medium text-red-600">Error:</span>{' '}
              <span className="text-red-600">{result.error}</span>
            </div>
          )}

          <div className="mt-3 p-3 bg-white rounded border">
            <h5 className="font-medium mb-2">Debug Information</h5>
            <pre className="text-xs text-gray-700 overflow-x-auto">
{JSON.stringify(
  {
    timestamp: result.timestamp,
    edgeUrl,
    rpcResult: result.rpcResult ?? null,
    httpStatus: result.httpStatus ?? null,
    debug: result.debug || null,
  },
  null,
  2
)}
            </pre>
          </div>

          <div className="mt-3 text-xs text-gray-500">
            <p><strong>Troubleshooting tips</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-1">
              <li>Log in as a user with <code>platform_admin</code> in <code>user_profiles</code>.</li>
              <li>Ensure the RPC has <code>SECURITY DEFINER</code> and <code>GRANT EXECUTE TO anon, authenticated</code>.</li>
              <li>Edge Function secrets must be set: <code>SUPABASE_URL</code>, <code>SUPABASE_ANON_KEY</code>, <code>SUPABASE_SERVICE_ROLE_KEY</code>.</li>
              <li>Expect 403 from the Edge Function if the caller is not a platform admin.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformAdminTester;
